import React, { useState, useEffect } from 'react';
import { apiService, User, Ticket } from '../services/api';
import './UserProfile.css';

interface UserProfileProps {
  userEmail: string;
  onClose: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ userEmail, onClose }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    dateOfBirth: ''
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const [userData, userTickets] = await Promise.all([
          apiService.getUserByEmail(userEmail),
          apiService.getUserTickets(userEmail)
        ]);
        
        setUser(userData);
        setTickets(userTickets);
        if (userData) {
        setFormData({
          name: userData.name || '',
          phone: userData.phone || '',
          dateOfBirth: userData.dateOfBirth || ''
        });
      }
      } catch (err) {
        setError('Failed to load user data');
        console.error('Error fetching user data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userEmail]);

  const handleSave = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const updatedUser = await apiService.updateUser(user.id, formData);
      setUser(updatedUser);
      setEditing(false);
    } catch (err) {
      setError('Failed to update user information');
      console.error('Error updating user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading">Loading user profile...</div>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>User Profile</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>User Profile</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="user-info-section">
            <div className="section-header">
              <h3>Personal Information</h3>
              {!editing ? (
                <button 
                  className="btn btn-secondary btn-small"
                  onClick={() => setEditing(true)}
                >
                  Edit
                </button>
              ) : (
                <div className="edit-actions">
                  <button 
                    className="btn btn-secondary btn-small"
                    onClick={() => {
                      setEditing(false);
                      setFormData({
                        name: user?.name || '',
                        phone: user?.phone || '',
                        dateOfBirth: user?.dateOfBirth || ''
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary btn-small"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    Save
                  </button>
                </div>
              )}
            </div>

            <div className="user-details">
              <div className="form-group">
                <label htmlFor="user-email">Email Address</label>
                <input 
                  id="user-email"
                  type="email" 
                  value={user?.email || ''} 
                  disabled 
                  title="Email address (read-only)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="user-name">Full Name</label>
                {editing ? (
                  <input
                    id="user-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter your full name"
                  />
                ) : (
                  <input 
                    id="user-name-display"
                    type="text" 
                    value={user?.name || 'Not provided'} 
                    disabled 
                    title="Full name (read-only)"
                  />
                )}
              </div>

              <div className="form-group">
                <label htmlFor="user-phone">Phone Number</label>
                {editing ? (
                  <input
                    id="user-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter your phone number"
                  />
                ) : (
                  <input 
                    id="user-phone-display"
                    type="text" 
                    value={user?.phone || 'Not provided'} 
                    disabled 
                    title="Phone number (read-only)"
                  />
                )}
              </div>

              <div className="form-group">
                <label htmlFor="user-dob">Date of Birth</label>
                {editing ? (
                  <input
                    id="user-dob"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  />
                ) : (
                  <input 
                    id="user-dob-display"
                    type="text" 
                    value={user?.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : 'Not provided'} 
                    disabled 
                    title="Date of birth (read-only)"
                  />
                )}
              </div>

              <div className="form-group">
                <label htmlFor="user-created">Member Since</label>
                <input 
                  id="user-created"
                  type="text" 
                  value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : ''} 
                  disabled 
                  title="Member since date (read-only)"
                />
              </div>
            </div>
          </div>

          <div className="tickets-section">
            <h3>My Tickets ({tickets.length})</h3>
            {tickets.length === 0 ? (
              <p className="no-tickets">No tickets purchased yet.</p>
            ) : (
              <div className="tickets-list">
                {tickets.map(ticket => (
                  <div key={ticket.id} className="ticket-item">
                    <div className="ticket-info">
                      <span className="ticket-id">#{ticket.id.slice(-8)}</span>
                      <span className="ticket-event">Event: {ticket.eventId}</span>
                      <span className="ticket-price">${ticket.finalPrice}</span>
                    </div>
                    <div className="ticket-meta">
                      <span className={`ticket-status status-${ticket.status}`}>
                        {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                      </span>
                      <span className="ticket-date">
                        {new Date(ticket.purchaseDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
